import { useDismissableOverlay } from '@catering/ui';
import { Link } from '@tanstack/react-router';
import { Bell, ChevronDown, LogOut, ShieldCheck } from 'lucide-react';
import { type JSX, useId, useState } from 'react';

export interface UserMenuProps {
  fullName: string;
  email: string;
  onLogout: () => void;
}

function initials(fullName: string): string {
  const [first, ...rest] = fullName.trim().split(/\s+/).filter(Boolean);
  const last = rest[rest.length - 1];

  return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase();
}

export function UserMenu({ fullName, email, onLogout }: UserMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const { containerRef } = useDismissableOverlay({ open, onClose: () => setOpen(false) });

  function close(): void {
    setOpen(false);
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        className="flex items-center gap-3 rounded-full py-1 pl-1 pr-3 text-left transition-colors hover:bg-slate-100"
      >
        <span
          aria-hidden="true"
          className="flex size-9 items-center justify-center rounded-full bg-primary-600 text-sm font-semibold text-white"
        >
          {initials(fullName)}
        </span>
        <span className="hidden text-sm sm:block">
          <span className="block font-medium text-slate-900">{fullName}</span>
          <span className="block text-slate-500">{email}</span>
        </span>
        <ChevronDown aria-hidden="true" className="size-4 shrink-0 text-slate-400" />
      </button>

      {open ? (
        <div
          id={menuId}
          role="menu"
          aria-label="Menú de usuario"
          className="absolute right-0 z-20 mt-2 w-56 rounded-lg border border-slate-200 bg-white py-1 shadow-md"
        >
          <Link
            to="/account/security"
            role="menuitem"
            onClick={close}
            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            <ShieldCheck aria-hidden="true" className="size-4" />
            Cuenta / Seguridad
          </Link>
          <Link
            to="/account/notifications"
            role="menuitem"
            onClick={close}
            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            <Bell aria-hidden="true" className="size-4" />
            Notificaciones
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              close();
              onLogout();
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
          >
            <LogOut aria-hidden="true" className="size-4" />
            Cerrar sesión
          </button>
        </div>
      ) : null}
    </div>
  );
}
